import {
  ProductType,
  updateQs,
  useBulkQuotation,
  useDefaultCouponCode,
  useLocalStorage,
  useProductSumAssuredRangeMapping,
} from '@bowtie-web/common';
import { Box } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Skeleton } from '@material-ui/lab';
import { pick } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { APP_START_URL_LOCAL_STORAGE_KEY } from '../constants';
import { useAttributeAndQsContext } from '../contexts/AttributeAndQsContext';
import { useQuotationContext } from '../contexts/QuotationContext';
import { useUserData } from '../contexts/UserAuthContext';
import { isSumAssuredRequired } from '../helpers';
import { getAppStartPath, sortAndStringifyQs } from '../helpers/queryparam';
import theme from '../helpers/theme';
import useCouponOptions from '../hooks/useCouponOptions';
import useRidersSelection from '../hooks/useRidersSelection';
import ActionPanel from './ActionPanel/ActionPanel';
import CouponForm from './CouponForm';
import ErrorMessagePanel from './ErrorMessagePanel';
import FormPanel from './FormPanel/FormPanel';
import RecommendationAnchor from './MarketingPageAnchorLink';
import { ANCHOR_LINK_VARIANT_MAPPINGS } from './MarketingPageAnchorLink/MarketingPageAnchorLink';
import PromotionBanner from './PromotionBanner/PromotionBanner';
import ResultPanel from './ResultPanel';
import RidersSelectionPanel from './RidersSelectionPanel/RidersSelectionPanel';
import TermAndConditions from './TermAndConditions';
import VariableAttributeButtons from './VariableAttributeButtons/VariableAttributeButtons';

const QuotationWidget = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const {
    activeProduct,
    products: productsBase,
    variables,
    values,
    handleChange,
    selectedVariable,
    setActiveProduct,
    setSelectedVariable,
    isIssueAgeValid,
    coupon,
    setCoupon,
    isSumAssuredValid,
    bulkQuotation,
    activeProductQuotation,
    productType,
    socialProof,
    rawProducts,
    enableShowingAnnualLevelTermLife,
    termLifeYear,
  } = useQuotationContext();

  const allQs = useAttributeAndQsContext();
  const {
    riderOptions,
    button,
    size: sizeProp = 'responsive',
    showRecommendationAnchor,
    shouldShowBanner,
    variableAttribute,
    shouldShowVariableAttributeButtons,
    anchorLinkType,
    defaultCoupon,
    defaultCouponType,
    publicDefaultCouponTypes,
    existingCustomerDefaultCouponTypes,
    defaultRiderOption,
  } = allQs;

  // Use isExistingCustomer from postMessage instead of query param
  const { isExistingCustomer } = useUserData();
  const isLargeInResponsive = useMediaQuery(theme.breakpoints.up('sm'));

  const size = useMemo(
    () =>
      sizeProp === 'responsive'
        ? isLargeInResponsive
          ? 'large'
          : 'small'
        : sizeProp,
    [isLargeInResponsive, sizeProp],
  );

  // A temporary method to filter BB-CI or Juvenile CI if there's regular CI product
  const products = useMemo(() => {
    const regularCiProductCodes = ['term-ci', 'term-ci-plus', 'term-ci-max'];
    const regularTermCiIncludedInDisplayProducts = productsBase.some(
      ({ code }) => regularCiProductCodes.includes(code),
    );
    if (!regularTermCiIncludedInDisplayProducts) {
      return productsBase;
    }
    const bbCiOrJuvenileCiProductCodes = ['bb-ci', 'juvenile-ci'];
    return productsBase.filter(
      ({ code }) => !bbCiOrJuvenileCiProductCodes.includes(code),
    );
  }, [productsBase]);

  const { riders, selectedRiders, onSelectedRiderChange } = useRidersSelection({
    productCodes: products.map(({ code }) => code),
    riderCodes: riderOptions,
    activeProduct: activeProduct?.code,
    disable: !riderOptions,
    multiple: false,
    defaultRiderOption,
  });

  const demographicData = useMemo(
    () => pick(values, ['age', 'gender', 'smoker']),
    [values],
  );

  const [riderSumAssuredRanges] = useProductSumAssuredRangeMapping(
    riders,
    values.age,
  );

  const [riderDetailAndQuotations, { loading: loadingRidersQuotation }] =
    useBulkQuotation(
      {
        products: riders.map(code => {
          // check the ranges
          return {
            code,
            sum_assured:
              riderSumAssuredRanges &&
              riderSumAssuredRanges.size > 0 &&
              isSumAssuredRequired(code)
                ? // For now the only use case of rider sum_assured is last wish, where min = max
                  riderSumAssuredRanges.get(code)?.max_sum_assured
                : undefined,
          };
        }),
        ...demographicData,
        coupon_code: coupon?.promo_code,
      },
      { skip: !activeProduct },
    );

  const appStartUrl = useMemo(() => {
    if (!activeProduct) {
      return '';
    }
    /**
     * This generates the following product query
     * e.g.
     * ?products[0][sum_assured]=200000&products[1][product_id]=last-wish-rider&products[1][sum_assured]=300000
     */
    const productQs = [activeProduct.code, ...selectedRiders].reduce(
      (a, v, i) => ({
        ...a,
        [`products[${i}][product_id]`]: v,
        ...(isSumAssuredRequired(v) && {
          [`products[${i}][sum_assured]`]:
            i === 0
              ? values.sumAssured
              : riderSumAssuredRanges.get(v)?.max_sum_assured,
        }),
      }),
      {},
    );

    const qs = sortAndStringifyQs({
      coupon: coupon ? coupon.promo_code : '',
      ...values,
      ...allQs,
      ...productQs,
      // termLifeYear is for quotation FE use only
      termLifeYear: undefined,
      // remove the sum assured from the query string
      sumAssured: undefined,
    });

    return getAppStartPath(qs, intl.locale);
  }, [
    activeProduct,
    allQs,
    coupon,
    intl.locale,
    riderSumAssuredRanges,
    selectedRiders,
    values,
  ]);

  const termAndConditionProducts = [
    ...products,
    ...riderDetailAndQuotations.map(detail => detail.product),
  ];

  const onClickCta = useCallback(() => {
    if (appStartUrl) {
      window.open(appStartUrl, '_blank');
    }
  }, [appStartUrl]);

  const [, setUrl] = useLocalStorage(
    APP_START_URL_LOCAL_STORAGE_KEY,
    appStartUrl,
  );

  const handleVariableAttributeChange = (params: Record<string, string>) => {
    history.push({
      ...location,
      search: updateQs(location.search, {
        ...params,
      }),
    });
  };

  const [defaultCouponCode, { loading: loadingDefaultCouponCode }] =
    useDefaultCouponCode(defaultCoupon, defaultCouponType);

  const [publicCodesMapping] = useCouponOptions(
    products.map(({ code }) => code),
    publicDefaultCouponTypes?.split(',') || [],
  );

  const [existingCustomerCodesMapping] = useCouponOptions(
    products.map(({ code }) => code),
    existingCustomerDefaultCouponTypes
      ? existingCustomerDefaultCouponTypes.split(',')
      : [],
  );

  const showTermLifeSlider = useMemo(
    () =>
      productType === ProductType.LIFE && !!enableShowingAnnualLevelTermLife,
    [productType, enableShowingAnnualLevelTermLife],
  );

  const shouldShowNonTermLifeAnchorLink = useMemo(
    () =>
      anchorLinkType !== 'term-level-life-premium-explanation' &&
      (showRecommendationAnchor === 'true' ||
        Object.keys(ANCHOR_LINK_VARIANT_MAPPINGS).includes(anchorLinkType)),
    [anchorLinkType, showRecommendationAnchor],
  );

  useEffect(() => {
    setUrl(appStartUrl);
  }, [appStartUrl, setUrl]);

  return (
    <Box
      width="100%"
      maxWidth={size === 'small' ? 450 : 800}
      minWidth={size === 'small' ? 0 : 600}
    >
      {shouldShowVariableAttributeButtons === 'true' && (
        <VariableAttributeButtons
          size={size}
          variableAttribute={variableAttribute}
          onClick={handleVariableAttributeChange}
        />
      )}
      <Box p={size === 'small' ? 2 : 3} bgcolor="white">
        <PromotionBanner
          promotion={coupon}
          collapse={shouldShowBanner !== 'true'}
          size={size}
        />
        <Box display="grid" alignItems="stretch" gridGap={16}>
          <FormPanel
            activeProduct={activeProduct}
            rawProducts={rawProducts}
            variables={variables}
            formValues={values}
            onFormValuesChange={handleChange}
            selectedVariable={selectedVariable}
            onSelectVariable={setSelectedVariable}
            setActiveProduct={setActiveProduct}
            onVariableAttributeChange={handleVariableAttributeChange}
            showTermLifeSlider={showTermLifeSlider}
            termLifeYear={termLifeYear}
            anchorLinkType={anchorLinkType}
          />

          {shouldShowNonTermLifeAnchorLink &&
            (activeProduct ? (
              <RecommendationAnchor
                productType={activeProduct.type}
                variant={
                  showRecommendationAnchor === 'true'
                    ? 'recommendation'
                    : anchorLinkType
                }
              />
            ) : (
              <Skeleton width="250px" variant="text" />
            ))}

          {activeProduct ? (
            <ResultPanel
              onActiveProductChange={setActiveProduct}
              activeProduct={activeProduct}
              formValues={values}
              coupon={coupon}
              products={products}
              size={size}
              isValid={isIssueAgeValid && isSumAssuredValid}
              bulkQuotation={bulkQuotation}
              socialProof={socialProof}
            />
          ) : (
            <Skeleton
              width="100%"
              height="200px"
              variant="rect"
              style={{ borderRadius: 8 }}
            />
          )}

          {riderOptions && (
            <RidersSelectionPanel
              size={size}
              riderDetailAndQuotations={riderDetailAndQuotations}
              selectedRiders={selectedRiders}
              onSelect={onSelectedRiderChange}
              isValid={isIssueAgeValid && isSumAssuredValid}
              loading={riderDetailAndQuotations.length === 0}
            />
          )}

          {activeProduct && !loadingDefaultCouponCode ? (
            <CouponForm
              couponCode={defaultCouponCode.toUpperCase() || ''}
              coupon={coupon}
              onCouponChange={setCoupon}
              isIssueAgeValid={isIssueAgeValid}
              activeProduct={activeProduct}
              publicCouponOptions={publicCodesMapping[activeProduct?.code]}
              existingCustomerCouponOptions={
                isExistingCustomer
                  ? existingCustomerCodesMapping[activeProduct?.code]
                  : undefined
              }
              productCodes={products.map(({ code }) => code)}
            />
          ) : (
            <Skeleton
              width="100%"
              height="95px"
              variant="rect"
              style={{ borderRadius: 4 }}
            />
          )}

          {activeProduct && (
            <ErrorMessagePanel
              formValues={values}
              isIssueAgeValid={isIssueAgeValid}
            />
          )}

          <ActionPanel
            activeProduct={activeProduct}
            formValues={values}
            disabled={
              !isIssueAgeValid ||
              !isSumAssuredValid ||
              loadingRidersQuotation ||
              !activeProduct ||
              !activeProductQuotation ||
              !appStartUrl
            }
            size={size}
            variant={
              button === '2'
                ? productType === ProductType.VHIS
                  ? 'plans-comparison'
                  : 'plan-detail'
                : 'cta-only'
            }
            onClick={onClickCta}
          />

          <TermAndConditions
            products={termAndConditionProducts}
            socialProof={socialProof}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default QuotationWidget;
